import { useMutation, useQuery } from '@tanstack/react-query';
import {
	createUser,
	deleteUserById,
	getConditions,
	getUserAppointments,
	getUserById,
	getUserByPhone,
	getUserEducationAssignments,
	getUsers,
	updateUserById,
} from '../../api/user/userApi';
import { queryClient } from '../../../lib/query';

export const useGetUsers = (params?: Partial<Parameters<typeof getUsers>[0]>) => {
	const resolvedParams = {
		name: params?.name,
		page: params?.page ?? 1,
		limit: params?.limit ?? 10,
	};

	return useQuery({
		queryKey: ['users', resolvedParams],
		queryFn: async () => {
			const response = await getUsers(resolvedParams);
			return response;
		},
	});
};

export const useGetUserById = (id?: number) => {
	return useQuery({
		queryKey: ['user', id],
		queryFn: async () => {
			if (typeof id !== 'number') throw new Error('User id is required');
			const response = await getUserById(id);
			return response;
		},
		enabled: typeof id === 'number',
	});
};

export const useGetUserByPhone = (phone?: string) => {
	return useQuery({
		queryKey: ['user-by-phone', phone],
		queryFn: async () => {
			if (!phone) throw new Error('Phone is required');
			const response = await getUserByPhone(phone);
			return response;
		},
		enabled: !!phone,
	});
};

export const useGetUserAppointments = (id?: number) => {
	return useQuery({
		queryKey: ['user-appointments', id],
		queryFn: async () => {
			if (typeof id !== 'number') throw new Error('User id is required');
			const response = await getUserAppointments(id);
			return response;
		},
		enabled: typeof id === 'number',
	});
};

export const useCreateUser = () => {
	return useMutation({
		mutationKey: ['create-user'],
		mutationFn: async (payload: Parameters<typeof createUser>[0]) => {
			const response = await createUser(payload);
			return response;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
		},
	});
};

export const useUpdateUserById = () => {
	return useMutation({
		mutationKey: ['update-user'],
		mutationFn: async (args: { id: number; payload: Parameters<typeof updateUserById>[1] }) => {
			const response = await updateUserById(args.id, args.payload);
			return response;
		},
		onSuccess: async (_data, variables) => {
			await queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
			await queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
			await queryClient.invalidateQueries({ queryKey: ['user-appointments', variables.id] });
		},
	});
};

export const useDeleteUserById = () => {
	return useMutation({
		mutationKey: ['delete-user'],
		mutationFn: async (id: number) => {
			await deleteUserById(id);
		},
		onSuccess: async (_data, id) => {
			await queryClient.invalidateQueries({ queryKey: ['users'], exact: false });
			await queryClient.invalidateQueries({ queryKey: ['user', id] });
			await queryClient.invalidateQueries({ queryKey: ['user-appointments', id] });
		},
	});
};

export const useGetConditions = (params: { limit: number }) => {
	const resolvedParams = {
		limit: params.limit,
	};

	return useQuery({
		queryKey: ['conditions', resolvedParams],
		queryFn: async () => {
			const response = await getConditions(resolvedParams);
			return response;
		},
	});
};

export const useGetUserEducationAssignments = (params: Partial<Parameters<typeof getUserEducationAssignments>[0]>) => {
	const resolvedParams = {
		id: params.id ?? 0,
		page: params.page ?? 1,
		limit: params.limit ?? 10,
		status: params.status,
	};

	return useQuery({
		queryKey: ['user-education-assignments', resolvedParams],
		queryFn: async () => {
			if (typeof resolvedParams.id !== 'number' || resolvedParams.id === 0) {
				throw new Error('User id is required');
			}
			const response = await getUserEducationAssignments(resolvedParams as Parameters<typeof getUserEducationAssignments>[0]);
			return response;
		},
		enabled: typeof params.id === 'number' && params.id !== 0,
	});
};

